import { getUserRoles } from '@/utils/stateAccessors';
import { FC, useMemo, type JSX } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getAcceptedFileTypes } from '@payaca/helpers/fileHelper';
import { roundToDecimalPlaces } from '@payaca/helpers/numberHelper';
import { userHasRequiredPermission } from '@payaca/permissions/permissions.utils';
import { TemplatesPermissions } from '@payaca/permissions/templates/templates.permissions';
import { UploadsPermissions } from '@payaca/permissions/uploads/uploads.permissions';
import { updateTemplate } from '@payaca/store/templates/templatesActions';
import {
  PublicEntityTemplate,
  PublicHydratedEntityTemplate,
  TemplatableEntity,
} from '@payaca/types/entity-templates';
import UntitledIcon from '@payaca/untitled-icons';
import { getTimeDuration } from '@payaca/utilities/timeUtilities';
import { WithOptional } from '@payaca/utilities/types';
import moment from 'moment-timezone';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import TagSelectionControl from '../tagSelectionControl/TagSelectionControl';
import UploadCollectionControl from '../uploadCollectionControl/UploadCollectionControl';
import './EntityTemplateReadView.sass';
import './EntityTemplateScheduledEventReadView.sass';
import ScheduledEventLinkedTaskTemplatesPanel from './ScheduledEventLinkedTaskTemplatesPanel';

const acceptFileTypes = getAcceptedFileTypes(['document', 'image', 'video']);

type Props = {
  entityTemplate: PublicHydratedEntityTemplate<'scheduledEvent'>;
  onUpdateEntityTemplateSuccess?: (
    updatedEntityTemplate: PublicHydratedEntityTemplate<TemplatableEntity>
  ) => void;
};
const EntityTemplateScheduledEventReadView: FC<Props> = ({
  entityTemplate,
  onUpdateEntityTemplateSuccess,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();

  const userRoles = useSelector(getUserRoles);
  const userCanDeleteAttachment = useMemo(() => {
    return userHasRequiredPermission(userRoles, [
      UploadsPermissions.DELETE_UPLOAD,
    ]);
  }, [userRoles]);

  const duration = useMemo<any>(() => {
    if (!entityTemplate?.data?.duration) return null;
    return getTimeDuration(
      moment().subtract(entityTemplate.data.duration).toDate(),
      moment().toDate()
    );
  }, [entityTemplate?.data.duration]);

  if (!entityTemplate) return null;

  return (
    <div className={'entity-template-scheduled-event-read-view'}>
      <PermissionGuard
        renderIfHasPermissions={[TemplatesPermissions.PERSIST_TEMPLATE]}
      >
        <TagSelectionControl
          selectedTagIds={
            (
              entityTemplate as WithOptional<
                PublicEntityTemplate<'scheduledEvent'>,
                'publicId'
              >
            ).data?.tagIds || []
          }
          onSelectedTagsChange={(tagIds) => {
            dispatch(
              updateTemplate.request({
                publicId: entityTemplate.publicId,
                template: {
                  entityType: entityTemplate.entityType,
                  data: {
                    ...entityTemplate.data,
                    tagIds: tagIds,
                  },
                },
                callback: onUpdateEntityTemplateSuccess,
              })
            );
          }}
        />
      </PermissionGuard>
      <div>
        {entityTemplate?.data.name && (
          <h1 className="name">{entityTemplate.data.name}</h1>
        )}
        {duration && (
          <span className="duration">
            <span className="label">Duration:</span>{' '}
            {roundToDecimalPlaces(duration.value)} {duration.label}
          </span>
        )}
      </div>
      {entityTemplate?.data.description && (
        <div className="description">
          <div className="label">Description</div>
          <div>{entityTemplate.data.description}</div>
        </div>
      )}
      {entityTemplate?.data.includeCustomerInformation && (
        <div className="include-customer-information">
          <UntitledIcon name="check-square.3" />
          <span>
            Customer information will be shown against the Event where it is
            available
          </span>
        </div>
      )}
      <div className="tasks">
        <div className="label">Add linked Tasks</div>
        <ScheduledEventLinkedTaskTemplatesPanel
          taskTemplates={entityTemplate.data?.tasks || []}
          onChange={(taskTemplates) => {
            return new Promise((resolve, reject) => {
              dispatch(
                updateTemplate.request({
                  publicId: entityTemplate.publicId,
                  template: {
                    entityType: entityTemplate.entityType,
                    data: {
                      ...entityTemplate.data,
                      tasks: taskTemplates,
                    },
                  },
                  callback: (et) => {
                    onUpdateEntityTemplateSuccess?.(et);
                    resolve();
                  },
                })
              );
            });
          }}
        />
      </div>
      <PermissionGuard
        renderIfHasPermissions={[UploadsPermissions.GET_UPLOADS]}
      >
        <UploadCollectionControl
          label="Attachments"
          uploadIds={entityTemplate.data?.uploadIds || []}
          onUploadCollectionChange={(uploadIds) => {
            dispatch(
              updateTemplate.request({
                publicId: entityTemplate.publicId,
                template: {
                  entityType: entityTemplate.entityType,
                  data: {
                    ...entityTemplate.data,
                    uploadIds: uploadIds,
                  },
                },
                callback: onUpdateEntityTemplateSuccess,
              })
            );
          }}
          allowMultipleUploads={true}
          canRemove={userCanDeleteAttachment}
          acceptFileTypes={acceptFileTypes}
        />
      </PermissionGuard>
    </div>
  );
};
export default EntityTemplateScheduledEventReadView;
